/**
 * AI Chat API
 * RTK Query endpoints for AI-powered property assistance
 */

import { baseApi } from "./baseApi";

/**
 * AI Chat request interface
 */
export interface AIChatRequest {
	message: string;
	property_id?: string;
	conversation_id?: string;
}

/**
 * AI Chat response interface
 */
export interface AIChatResponse {
	success: boolean;
	message: string;
	conversation_id: string;
	property_id?: string;
	tokens_used?: number;
}

/**
 * AI Message interface
 */
export interface AIMessage {
	role: "user" | "assistant";
	content: string;
	timestamp: string;
}

/**
 * AI Conversation interface
 */
export interface AIConversation {
	id: string;
	user_id?: string;
	user_name?: string;
	property_id?: string;
	property_name?: string;
	messages: AIMessage[];
	created_at: string;
	updated_at: string;
}

/**
 * AI Conversation list item interface
 */
export interface AIConversationListItem {
	id: string;
	property_id?: string;
	property_name?: string;
	message_count: number;
	last_message?: {
		role: string;
		content: string;
		timestamp: string;
	};
	created_at: string;
	updated_at: string;
}

/**
 * Property review request interface
 */
export interface PropertyReviewRequest {
	property_id: string;
}

/**
 * Property review response interface
 */
export interface PropertyReviewResponse {
	success: boolean;
	review: string;
	property_id: string;
	tokens_used?: number;
}

/**
 * AI Chat API endpoints
 */
export const aiChatApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Send a message to AI assistant
		 */
		chat: builder.mutation<AIChatResponse, AIChatRequest>({
			query: (data) => ({
				url: "/ai/chat/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: (result) => [
				{ type: "AIConversations" as const, id: "LIST" },
				...(result?.conversation_id
					? [{ type: "AIConversations" as const, id: result.conversation_id }]
					: []),
			],
		}),

		/**
		 * Get user's AI conversations
		 */
		getConversations: builder.query<
			AIConversationListItem[],
			void
		>({
			query: () => ({
				url: "/ai/conversations/",
				method: "GET",
			}),
			transformResponse: (response: AIConversationListItem[] | { results: AIConversationListItem[] }) => {
				// Handle both array and object response formats
				if (Array.isArray(response)) {
					return response;
				}
				return response.results || [];
			},
			providesTags: ["AIConversations"],
		}),

		/**
		 * Get conversation details with messages
		 */
		getConversation: builder.query<AIConversation, string>({
			query: (id) => ({
				url: `/ai/conversations/${id}/`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "AIConversations", id }],
		}),

		/**
		 * Get AI-generated property review
		 */
		getPropertyReview: builder.mutation<
			PropertyReviewResponse,
			PropertyReviewRequest
		>({
			query: (data) => ({
				url: "/ai/property_review/",
				method: "POST",
				body: data,
			}),
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useChatMutation,
	useGetConversationsQuery,
	useGetConversationQuery,
	useGetPropertyReviewMutation,
} = aiChatApi;
